import React, { useState, useEffect } from "react";
import { Modal, Radio, DatePicker, Button, Input, message, Form, Image, UploadFile } from "antd";
import moment from "moment";
import { externalBillService } from "../externalBillService";
import { momentFormats } from "@/utils/utils";
import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";
import Upload, { UploadChangeParam } from "antd/lib/upload";
import { getBaseImgLookUrl, getBaseImgUploadUrl, getBaseUploadData } from "@/utils/constants";
import { fileDownload } from "@/utils/exportFile";


interface CompleteModalProps {
    visible: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    billVal: any;
}

export const CompleteModal = ({
    visible,
    onCancel,
    onConfirm,
    billVal,
}: CompleteModalProps) => {
    // 状态管理
    const [oldPartConfirm, setOldPartConfirm] = useState<number>(0);
    const [backTime, setBackTime] = useState<moment.Moment | null>(null);
    const [oldPartConfirmNote, setOldPartConfirmNote] = useState<string>("");
    const [isTimeout, setIsTimeout] = useState<boolean>(false);
    const [timeoutReason, setTimeoutReason] = useState<string>("");
    const { TextArea } = Input;
    // 组件挂载后检查是否超时
    useEffect(() => {
        if (billVal && billVal.expectedCompletionTime) {
            const currentTime = moment();
            const expectedTime = moment(momentFormats(billVal.expectedCompletionTime));
            setIsTimeout(currentTime.isAfter(expectedTime));
        }
    }, [billVal]);

    // 处理旧件返还选择
    const handleOldPartConfirmChange = (e: any) => {
        setOldPartConfirm(Number(e.target.value));
    };

    // 处理回厂时间选择
    const handleBackTimeChange = (time: moment.Moment | null) => {
        setBackTime(time);
    };

    // 处理情况说明输入
    const handleOldPartConfirmNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setOldPartConfirmNote(e.target.value);
    };

    // 处理超时原因输入
    const handleTimeoutReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTimeoutReason(e.target.value);
    };



    const [fileList, setFileList] = useState<UploadFile[]>([]);

    // 确认提交
    const handleConfirm = async () => {
        // 验证
        if (!backTime) {
            message.warning("请选择回厂时间");
            return;
        }

        if (oldPartConfirm === 0 && fileList.length === 0) {
            message.warning("请至少上传一张旧件照片");
            return;
        }

        if (isTimeout && !timeoutReason) {
            message.warning("请输入超时原因");
            return;
        }

        const values = {
            id: billVal.id,
            oldPartConfirm,
            backTime: moment(backTime).format('YYYY-MM-DD HH:mm:ss'),
            oldPartConfirmNote,
            ...(isTimeout && { timeoutReason }), // 如果超时，添加超时原因
        };

        try {

            const result = await externalBillService.completeExternalRepair(values);

            if (result.success) {
                onConfirm();
                message.success('完工成功');
            } else {
                message.warning('完工失败: ' + result || '未知错误');
            }
        } catch (error) {
            message.error('网络错误，请重试');
            console.error('完工确认出错:', error);
        }
    };
    const normFile = (e: UploadChangeParam) => {

        if (e?.file.status === 'done') {
            if (e?.file?.response?.retCode == 0) {
                const data: any = {
                    name: e.file.name,
                    size: e.file.size,
                    type: e.file.type,
                    uid: e?.file?.response?.retData,
                    status: 'upload',
                }
                // e.fileList = [data]
                return e.fileList
            } else {
                message.error('文件上传失败，请重新上传!')
                return e.fileList?.pop()
            }
        }
        if (e.file.status === 'error') {
            message.error('文件上传失败，请重新上传!')
            return e.fileList?.pop()
        }
        if (Array.isArray(e)) {
            return e
        }

    }
    const onPreview = (file: UploadFile<any>) => {
        const uid = file.response?.retData
        Modal.info({
            title: '图片查看(点击图片放大)',
            content: <Image style={{ width: '100%' }} src={getBaseImgLookUrl(uid)} />,
            okText: '确定',
            cancelText: '取消',
            centered: true,
            width: 800,
        })
    }
    return (
        <Modal
            title="完工确认"
            open={visible}
            onOk={handleConfirm}
            onCancel={onCancel}
            okText="确认完工"
            cancelText="取消"
        >
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                    <span style={{ fontWeight: "bold", width: 80, display: "inline-block" }}>
                        工单编号:
                    </span>
                    <span>{billVal.code}</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                    <span style={{ fontWeight: "bold", width: 80, display: "inline-block" }}>
                        预计完工:
                    </span>
                    <span>{moment(billVal.expectedCompletionTime).format('YYYY-MM-DD HH:mm')}</span>
                    {isTimeout && (
                        <span style={{ color: 'red', marginLeft: 10 }}>
                            <ExclamationCircleOutlined style={{ color: 'red' }} />已超时
                        </span>
                    )}
                </div>

                {isTimeout && (
                    <div style={{ display: "flex", marginBottom: 15, color: 'red' }}>
                        <span style={{ fontWeight: "bold", width: 80, display: "inline-block" }}>
                            超时原因:
                        </span>
                        <Input
                            style={{ width: '70%' }}
                            onChange={handleTimeoutReasonChange}
                            placeholder="请输入超时原因"
                        />
                    </div>
                )}

                <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                    <span style={{ fontWeight: "bold", width: 110, display: "inline-block" }}>
                        旧件是否返还:
                    </span>
                    <Radio.Group value={oldPartConfirm} onChange={handleOldPartConfirmChange}>
                        <Radio value={0}>是</Radio>
                        <Radio value={1}>否</Radio>
                    </Radio.Group>
                </div>
                {oldPartConfirm === 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <span style={{ fontWeight: "bold", width: 80, display: "inline-block", verticalAlign: "top", marginBottom: '10px' }}>
                            旧件照片:
                        </span>
                        <Form.Item name="fileList" valuePropName="fileList" getValueFromEvent={normFile} >
                            <Upload
                                onPreview={onPreview}
                                name="file"
                                action={getBaseImgUploadUrl()}
                                data={getBaseUploadData()}
                                listType="picture-card"
                                multiple
                                maxCount={9}
                                accept="image/*"
                                onChange={({ fileList: newFileList }) => {
                                    let newData = []
                                    newData = newFileList.map((item) => {
                                        return item?.response?.retData
                                    })
                                    setFileList(newData)

                                }}
                            >
                                {fileList.length < 9 && (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8 }}>上传照片</div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>
                    </div>
                )}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 15 }}>
                    <span style={{ fontWeight: "bold", width: 100, display: "inline-block" }}>
                        回厂时间:
                    </span>
                    <DatePicker
                        showTime
                        format="YYYY-MM-DD HH:mm:ss"
                        value={backTime}
                        onChange={handleBackTimeChange}
                    />
                </div>

                <div style={{ marginBottom: 20 }}>
                    <span style={{ fontWeight: "bold", width: 80, display: "inline-block", verticalAlign: "top" }}>
                        情况说明:
                    </span>
                    <TextArea
                        rows={4}
                        value={oldPartConfirmNote}
                        onChange={handleOldPartConfirmNoteChange}
                        placeholder="请输入完工情况说明..."
                        style={{ marginTop: 10 }}
                    />
                </div>
            </div>
        </Modal>
    );
};